import { useState } from 'react';
import type { ComponentProps } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@/shared/lib/utils';

const DialogTitle = DialogPrimitive.Title;

const DialogDescription = DialogPrimitive.Description;

const FADE =
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0';

// Il focus si restituisce a un elemento HTML o SVG: entrambi hanno `focus()`.
const activeElement = () => document.activeElement as HTMLElement | null;

const LAYOUT = {
  center: {
    overlay: 'grid place-items-center overflow-y-auto p-4',
    content: 'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
  },
  fullScreen: { overlay: 'flex', content: 'flex min-h-0 min-w-0 flex-1 flex-col' },
};

// Esc, sfondo e focus li governa il dialogo: chi lo usa non li sostituisce.
type OwnHandlers =
  'onEscapeKeyDown' | 'onPointerDownOutside' | 'onOpenAutoFocus' | 'onCloseAutoFocus';

type DialogProps = Omit<ComponentProps<typeof DialogPrimitive.Content>, OwnHandlers> & {
  open?: boolean;
  onClose?: () => void;
  closeOnOutsideClick?: boolean;
  layout?: keyof typeof LAYOUT;
  overlayClassName?: string;
};

/**
 * Una finestra modale: chi la usa la monta e la smonta (aperta finché è montata, come le
 * modali di Primer e Atlassian), oppure passa `open` per farla sfumare in chiusura. Nel
 * portale, col focus intrappolato e restituito a chi l'ha aperta. Esc chiude; il click sullo
 * sfondo solo con `closeOnOutsideClick`; senza `onClose` non si chiude affatto.
 */
function Dialog({
  open = true,
  onClose,
  closeOnOutsideClick = false,
  layout = 'center',
  className,
  overlayClassName = 'bg-backdrop/50',
  children,
  ...props
}: DialogProps) {
  // Chi ha il focus quando il dialogo si apre: letto nel render, prima che il focus entri.
  const [opener, setOpener] = useState(() => (open ? activeElement() : null));
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpener(activeElement());
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onClose && (() => onClose())}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn('fixed inset-0 z-50', LAYOUT[layout].overlay, FADE, overlayClassName)}
        >
          <DialogPrimitive.Content
            aria-modal="true"
            className={cn('outline-hidden', FADE, LAYOUT[layout].content, className)}
            onEscapeKeyDown={(event) => {
              // Esc su una combobox aperta chiude il suo elenco, non il dialogo.
              if (!onClose || (event.target as Element).getAttribute('aria-expanded') === 'true') {
                event.preventDefault();
              }
            }}
            onPointerDownOutside={(event) => {
              if (!onClose || !closeOnOutsideClick) event.preventDefault();
            }}
            // Sopra un altro dialogo l'`autoFocus` di React arriva prima che quello sotto molli il
            // focus, che torna indietro: l'elemento da focalizzare si segna con `data-autofocus`.
            onOpenAutoFocus={(event) => {
              const preferred = (event.currentTarget as Element).querySelector<HTMLElement>(
                '[data-autofocus]'
              );
              preferred?.focus();
              // Se non ha preso il focus (es. disabilitato), Radix sceglie il primo elemento.
              if (preferred && document.activeElement === preferred) event.preventDefault();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (opener?.isConnected) opener.focus();
            }}
            {...props}
          >
            {children}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export { Dialog, DialogDescription, DialogTitle };
